import { type FC, useState, useMemo } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { SANCTIONED_BY_COLORS, COUNTRY_GROUPS } from '@/services/sanctions';
import type { SanctionEntry } from '@/types';

// ---------------------------------------------------------------------------
// Sanctions Intelligence Panel — OFAC / EU / UN / UK style
// ---------------------------------------------------------------------------

const TYPE_ICONS: Record<SanctionEntry['type'], string> = {
  individual: '👤',
  entity:     '🏢',
  vessel:     '🚢',
  aircraft:   '✈️',
  country:    '🌍',
};

const SECTORS = ['Finance', 'Energy', 'Defense', 'Military', 'Intelligence', 'Shipping', 'Weapons', 'Tech', 'Government'];

const SanctionRow: FC<{ entry: SanctionEntry }> = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={clsx(
        'border rounded p-2 cursor-pointer transition-colors',
        entry.active
          ? 'border-red-900/40 bg-red-950/15 hover:bg-red-950/25'
          : 'border-slate-800/40 bg-slate-900/20 opacity-50',
      )}
      onClick={() => setExpanded((v) => !v)}
    >
      <div className="flex items-start gap-1.5">
        <span className="text-[12px] shrink-0">{TYPE_ICONS[entry.type]}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-semibold text-slate-100 truncate">{entry.name}</span>
            {entry.nameKo && (
              <span className="text-[9px] text-slate-500 shrink-0">{entry.nameKo}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span className="text-[9px] text-slate-500">{entry.country}</span>
            {entry.sector && (
              <span className="text-[8px] px-1 rounded bg-slate-800 text-slate-400">{entry.sector}</span>
            )}
            <div className="flex gap-0.5">
              {entry.sanctionedBy.map((body) => (
                <span
                  key={body}
                  className={clsx('text-[8px] font-bold', SANCTIONED_BY_COLORS[body] ?? 'text-slate-400')}
                >
                  {body}
                </span>
              ))}
            </div>
          </div>
        </div>
        <span className="text-[8px] text-slate-600 shrink-0">{entry.date.slice(0, 7)}</span>
      </div>

      {expanded && (
        <div className="mt-1.5 pt-1.5 border-t border-slate-800/40">
          <p className="text-[9px] text-slate-400 leading-relaxed">{entry.reason}</p>
        </div>
      )}
    </div>
  );
};

type FilterBody = 'all' | 'OFAC' | 'EU' | 'UN' | 'UK';

export const SanctionsPanel: FC = () => {
  const sanctions = useAppStore((s) => s.sanctions);
  const [countryGroup, setCountryGroup] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [bodyFilter, setBodyFilter] = useState<FilterBody>('all');
  const [sectorFilter, setSectorFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => sanctions.filter((s) => {
    if (typeFilter !== 'all' && s.type !== typeFilter) return false;
    if (bodyFilter !== 'all' && !s.sanctionedBy.includes(bodyFilter)) return false;
    if (sectorFilter !== 'all' && s.sector !== sectorFilter) return false;
    if (countryGroup !== 'all') {
      const group = COUNTRY_GROUPS[countryGroup];
      if (group && !group.codes.includes(s.countryCode ?? '')) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!s.name.toLowerCase().includes(q) && !s.country.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [sanctions, typeFilter, bodyFilter, sectorFilter, countryGroup, search]);

  const countByBody = (body: string) => sanctions.filter((s) => s.sanctionedBy.includes(body)).length;

  // Sectors that actually appear in the data
  const activeSectors = useMemo(() =>
    SECTORS.filter((sec) => sanctions.some((s) => s.sector === sec)),
    [sanctions],
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="panel-header shrink-0">
        <span className="panel-title">🚫 Sanctions Intelligence</span>
        <div className="flex items-center gap-2">
          <span className="text-[8px] px-1.5 py-0.5 rounded bg-red-950/60 text-red-300 border border-red-900/40">
            {sanctions.filter((s) => s.active).length} ACTIVE
          </span>
          <span className="text-[8px] text-slate-600">Updated 2026-03</span>
        </div>
      </div>

      {/* Sanction body stat chips */}
      <div className="grid grid-cols-4 gap-1 px-3 py-1.5 border-b border-slate-800/40 shrink-0">
        {(['OFAC', 'EU', 'UN', 'UK'] as const).map((body) => (
          <button
            key={body}
            onClick={() => setBodyFilter(bodyFilter === body ? 'all' : body)}
            className={clsx(
              'rounded p-1.5 text-center border transition-colors',
              bodyFilter === body
                ? 'bg-slate-700 border-slate-600'
                : 'bg-slate-900/40 border-slate-800/50 hover:bg-slate-800/40',
            )}
          >
            <div className={clsx('text-[11px] font-bold tabular-nums', SANCTIONED_BY_COLORS[body] ?? 'text-slate-400')}>
              {countByBody(body)}
            </div>
            <div className="text-[8px] text-slate-600">{body}</div>
          </button>
        ))}
      </div>

      {/* Country group filter */}
      <div className="flex gap-0.5 px-2 py-1 border-b border-slate-800/40 shrink-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        <button
          onClick={() => setCountryGroup('all')}
          className={clsx(
            'shrink-0 px-1.5 py-0.5 rounded text-[8px] font-medium transition-colors',
            countryGroup === 'all' ? 'bg-rose-900/50 text-rose-300' : 'text-slate-500 hover:text-slate-300',
          )}
        >All</button>
        {Object.entries(COUNTRY_GROUPS).map(([key, grp]) => (
          <button
            key={key}
            onClick={() => setCountryGroup(countryGroup === key ? 'all' : key)}
            className={clsx(
              'shrink-0 px-1.5 py-0.5 rounded text-[8px] font-medium transition-colors whitespace-nowrap',
              countryGroup === key ? 'bg-rose-900/50 text-rose-300' : 'text-slate-500 hover:text-slate-300',
            )}
          >
            {grp.label}
          </button>
        ))}
      </div>

      {/* Search + type + sector filters */}
      <div className="px-2 py-1 space-y-1 border-b border-slate-800/40 shrink-0">
        <input
          type="text"
          placeholder="Search name or country…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-slate-900/60 border border-slate-800 rounded px-2 py-1 text-[10px] text-slate-300 placeholder-slate-600 outline-none focus:border-slate-600"
        />
        {/* Type pills */}
        <div className="flex gap-0.5 flex-wrap">
          {(['all', 'individual', 'entity', 'vessel'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setTypeFilter(f)}
              className={clsx(
                'px-1.5 py-0.5 rounded text-[8px] font-medium transition-colors',
                typeFilter === f ? 'bg-rose-900/50 text-rose-300' : 'text-slate-500 hover:text-slate-300',
              )}
            >
              {f === 'all' ? 'All types' :
               f === 'individual' ? '👤 Person' :
               f === 'entity' ? '🏢 Entity' : '🚢 Vessel'}
            </button>
          ))}
        </div>
        {/* Sector pills */}
        <div className="flex gap-0.5 flex-wrap">
          <button
            onClick={() => setSectorFilter('all')}
            className={clsx(
              'px-1.5 py-0.5 rounded text-[8px] font-medium transition-colors',
              sectorFilter === 'all' ? 'bg-slate-700 text-slate-200' : 'text-slate-600 hover:text-slate-400',
            )}
          >All sectors</button>
          {activeSectors.map((sec) => (
            <button
              key={sec}
              onClick={() => setSectorFilter(sectorFilter === sec ? 'all' : sec)}
              className={clsx(
                'px-1.5 py-0.5 rounded text-[8px] font-medium transition-colors',
                sectorFilter === sec ? 'bg-slate-700 text-slate-200' : 'text-slate-600 hover:text-slate-400',
              )}
            >{sec}</button>
          ))}
        </div>
      </div>

      {/* Result count */}
      <div className="px-3 py-0.5 shrink-0">
        <span className="text-[8px] text-slate-600">
          {filtered.length} / {sanctions.length} entries
        </span>
      </div>

      {/* Sanctions list */}
      <div className="overflow-y-auto flex-1 p-2 space-y-1">
        {filtered.map((entry) => (
          <SanctionRow key={entry.id} entry={entry} />
        ))}
        {filtered.length === 0 && (
          <div className="flex items-center justify-center h-16 text-xs text-slate-600">No results found</div>
        )}
      </div>
    </div>
  );
};
